/**
 * Venpos admission product codes eligible for membership upgrade credit.
 * Adult codes credit adult/concession memberships; child codes credit child memberships.
 *
 * Castle membership (Friends of Belvoir): all eligible admission codes.
 * Parkland Adventure Pass: garden & playground + Flower & Garden Show admissions.
 */

export const CASTLE_MEMBERSHIP_PAGE_ID = '37bd858f-4f04-4e83-a469-05274db0d546';
export const PARKLAND_MEMBERSHIP_PAGE_ID = '9d7c060b-6088-4bc1-8def-7bddb9b87ef9';

const normalizeProductCode = (productCode) => (productCode || '').trim().toUpperCase();

export const ADULT_PRODUCT_CODES = [
  'ADMISS00003',
  'ADMISS00006',
  'ADMISS00007',
  'ADMISS00013',
  'ADMISS00118',
  'ADMISS00119', // Super Early Bird - Flower & Garden Show (Adult)
  'ADMISS00122', // Early Bird - Flower & Garden Show (Adult)
  'ADMISS00125', // Flower & Garden Show (Adult)
  'ADMISS00127', // Walk In - Flower & Garden Show (Adult)
  'ADMISS00138', // Circus - Adult
  'ADMISS00142', // Circus Activities Workshop
  'ADMISS00146', // Circus - Essential Carer
  'ADMISS00162',
  'ADMISS00163',
  'ADMISS00165',
  'ADMISS00167',
  'ADMISS00168',
  'ADMISS00170',
  'ADMISS00171',
  'ADMISS00173',
  'ADMISS00223',
  'ADMISS00233', // Early Bird Circus - Adult
  'ADMISS00235', // Early Bird Circus - Concession
  'ADMISS00236', // Circus - Concession
];

export const CHILD_PRODUCT_CODES = [
  'ADMISS00004',
  'ADMISS00008',
  'ADMISS00120', // Super Early Bird - Flower & Garden Show (Child)
  'ADMISS00123', // Early Bird - Flower & Garden Show (Child)
  'ADMISS00124', // Flower & Garden Show (Child)
  'ADMISS00126', // Walk In - Flower & Garden Show (Child)
  'ADMISS00139', // Circus - Child
  'ADMISS00140', // Circus - Under 3
  'ADMISS00164',
  'ADMISS00166',
  'ADMISS00169',
  'ADMISS00172',
  'ADMISS00234', // Early Bird Circus - Child
];

/** Codes eligible for Parkland Adventure Pass upgrades. */
export const GARDEN_PLAYGROUND_ADULT_PRODUCT_CODES = [
  'ADMISS00007', // Garden and Playground Only Entry - Adult
  'ADMISS00119', // Super Early Bird - Flower & Garden Show (Adult)
  'ADMISS00122', // Early Bird - Flower & Garden Show (Adult)
  'ADMISS00125', // Flower & Garden Show (Adult)
  'ADMISS00127', // Walk In - Flower & Garden Show (Adult)
];

export const GARDEN_PLAYGROUND_CHILD_PRODUCT_CODES = [
  'ADMISS00008', // Garden and Playground Only Entry - Child
  'ADMISS00120', // Super Early Bird - Flower & Garden Show (Child)
  'ADMISS00123', // Early Bird - Flower & Garden Show (Child)
  'ADMISS00124', // Flower & Garden Show (Child)
  'ADMISS00126', // Walk In - Flower & Garden Show (Child)
];

/** Whitelisted codes that stay eligible despite keywords in the description (e.g. workshop). */
const DESCRIPTION_FILTER_BYPASS_CODES = new Set(['ADMISS00142']);

export const isChildProductCode = (productCode) =>
  CHILD_PRODUCT_CODES.includes(normalizeProductCode(productCode));

export const isAdultProductCode = (productCode) =>
  ADULT_PRODUCT_CODES.includes(normalizeProductCode(productCode));

export const isDescriptionExcludedFromUpgrade = (productCode, productDescription = '') => {
  const code = normalizeProductCode(productCode);
  if (DESCRIPTION_FILTER_BYPASS_CODES.has(code)) {
    return false;
  }
  const description = (productDescription || '').toLowerCase();
  return (
    description.includes('afternoon tea') ||
    description.includes('archery') ||
    description.includes('activity') ||
    description.includes('experience')
  );
};

/**
 * Admission eligible for Parkland Adventure Pass (garden & playground and Flower &
 * Garden Show). Used to route upgrades and filter credit on the Parkland page.
 */
export const isGardenPlaygroundOnlyProductCode = (productCode, productDescription = '') => {
  const code = normalizeProductCode(productCode);
  if (
    GARDEN_PLAYGROUND_ADULT_PRODUCT_CODES.includes(code) ||
    GARDEN_PLAYGROUND_CHILD_PRODUCT_CODES.includes(code)
  ) {
    return true;
  }
  const description = (productDescription || '').toLowerCase();
  if (description.includes('castle')) {
    return false;
  }
  return (
    description.includes('garden and playground only') ||
    description.includes('gardens and playground only') ||
    description.includes('garden and adventure playground only') ||
    (description.includes('garden') &&
      description.includes('playground') &&
      description.includes('only'))
  );
};

const isPaidUpgradeItem = (item) => parseFloat(item?.value) > 0;

const isUpgradeChildLineItem = (item) => {
  const productCode = item.productCode || '';
  const description = (item.productDescription || '').toLowerCase();
  const isChildAdmissionCode = isChildProductCode(productCode);
  const isNotConcession = !description.includes('concession');
  const isEligibleDescription = !isDescriptionExcludedFromUpgrade(
    productCode,
    item.productDescription
  );
  return (
    isChildAdmissionCode &&
    isPaidUpgradeItem(item) &&
    isNotConcession &&
    isEligibleDescription
  );
};

const isUpgradeAdultLineItem = (item) => {
  const productCode = item.productCode || '';
  const description = (item.productDescription || '').toLowerCase();
  const isAdultAdmissionCode = isAdultProductCode(productCode);
  const isConcession = description.includes('concession');
  const isChildAdmissionCode = isChildProductCode(productCode);
  const isAdultOrConcession =
    isAdultAdmissionCode || (isChildAdmissionCode && isConcession);
  const isEligibleDescription = !isDescriptionExcludedFromUpgrade(
    productCode,
    item.productDescription
  );
  return isAdultOrConcession && isPaidUpgradeItem(item) && isEligibleDescription;
};

/** Any line that could count toward upgrade credit on the full castle membership journey. */
export const isUpgradeEligibleLineItem = (item) =>
  isUpgradeChildLineItem(item) || isUpgradeAdultLineItem(item);

/**
 * Whether this line counts for credit on the given membership page.
 * Parkland page: garden & playground tickets only. Castle page: all eligible lines.
 */
export const isUpgradeEligibleForMembershipPage = (item, membershipPageId) => {
  if (!isUpgradeEligibleLineItem(item)) {
    return false;
  }
  if (membershipPageId === PARKLAND_MEMBERSHIP_PAGE_ID) {
    return isGardenPlaygroundOnlyProductCode(item.productCode, item.productDescription);
  }
  return true;
};

/**
 * Pick castle vs parkland membership page from original booking lines.
 * All eligible lines garden-only → Parkland Adventure Pass; otherwise → Castle membership.
 */
export const resolveUpgradeMembershipPageId = (bookingItems) => {
  if (!Array.isArray(bookingItems) || bookingItems.length === 0) {
    return null;
  }
  const eligible = bookingItems.filter(isUpgradeEligibleLineItem);
  if (eligible.length === 0) {
    return null;
  }
  const allGardenOnly = eligible.every((item) =>
    isGardenPlaygroundOnlyProductCode(item.productCode, item.productDescription)
  );
  return allGardenOnly ? PARKLAND_MEMBERSHIP_PAGE_ID : CASTLE_MEMBERSHIP_PAGE_ID;
};

/**
 * Split booking lines into child/adult upgrade pools for a membership page.
 */
export const partitionUpgradeItemsForMembershipPage = (bookingItems, membershipPageId) => {
  const items = Array.isArray(bookingItems) ? bookingItems : [];
  const forPage = (item) => isUpgradeEligibleForMembershipPage(item, membershipPageId);

  const childItems = items
    .filter((item) => forPage(item) && isUpgradeChildLineItem(item))
    .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));

  const adultItems = items
    .filter((item) => forPage(item) && isUpgradeAdultLineItem(item))
    .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));

  return { childItems, adultItems };
};
